import { useCallback, useMemo, useState } from 'react';

import {
    type AutomationRunRowStatus,
    automationRunRowStatus,
    automationRunStatusQuery,
    matchesAutomationRunStatusFacets,
} from '@apps/web/components/automations/customer/automation_run_status';
import {
    useCoordinatorAutomationSkips,
    useOrganizationRuns,
} from '@apps/web/swr_data_fetching/data_fetching_hooks';

export type AutomationRunLedgerRow = {
    readonly id: string;
    readonly startedAt: string;
    readonly status: AutomationRunRowStatus;
    readonly isSkip: boolean;
    readonly attachmentCount: number;
    readonly suppressionReason: string | null;
};

function oldestStartedAt(rows: ReadonlyArray<AutomationRunLedgerRow>): number {
    return rows.reduce(
        (oldest, row) => Math.min(oldest, Date.parse(row.startedAt)),
        Number.POSITIVE_INFINITY,
    );
}

/**
 * One newest-first ledger of an automation's runs and, for an event
 * automation, the fires it suppressed. Only an event automation has skips,
 * and only the runs the selected facets can still show are read.
 *
 * The two reads page independently, so the ledger owns the page window: it
 * shows `pageSize` rows per page of the merged list, which keeps a page one
 * chronological page rather than one page from each source.
 */
export function useAutomationRunLedger({
    organizationId,
    automation,
    selectedFacets,
    pageSize,
}: {
    readonly organizationId: string;
    readonly automation: Readonly<{
        readonly id: string;
        readonly triggerType: 'schedule' | 'event';
    }>;
    readonly selectedFacets: ReadonlyArray<AutomationRunRowStatus>;
    readonly pageSize: number;
}) {
    const { id: automationId, triggerType } = automation;
    const runStatusQuery = useMemo(
        () => automationRunStatusQuery(selectedFacets),
        [selectedFacets],
    );
    const wantsRuns = runStatusQuery === undefined || runStatusQuery.length > 0;
    const wantsSkips =
        triggerType === 'event' && selectedFacets.includes('Skipped');

    const runs = useOrganizationRuns(
        wantsRuns ? organizationId : null,
        automationId,
        { limit: pageSize, status: runStatusQuery },
    );
    const skips = useCoordinatorAutomationSkips(organizationId, automationId, {
        enabled: wantsSkips,
        limit: pageSize,
    });

    // The page window restarts whenever the reads restart.
    const ledgerKey = [
        organizationId,
        automationId,
        runStatusQuery?.join(',') ?? 'every-status',
        wantsSkips,
    ].join('|');
    const [pageWindow, setPageWindow] = useState({
        key: ledgerKey,
        pageCount: 1,
    });
    const pageCount = pageWindow.key === ledgerKey ? pageWindow.pageCount : 1;

    const runRows = useMemo<Array<AutomationRunLedgerRow>>(
        () =>
            runs.data.map((run) => ({
                id: run.id,
                startedAt: run.created_at,
                status: automationRunRowStatus(run.status, run.needs_attention),
                isSkip: false,
                attachmentCount: run.attachment_count,
                suppressionReason: null,
            })),
        [runs.data],
    );
    const skipRows = useMemo<Array<AutomationRunLedgerRow>>(
        () =>
            wantsSkips
                ? skips.data.map((skip) => ({
                      id: skip.id,
                      startedAt: skip.event_occurred_at,
                      status: 'Skipped' as const,
                      isSkip: true,
                      attachmentCount: 0,
                      suppressionReason: skip.suppression_reason,
                  }))
                : [],
        [skips.data, wantsSkips],
    );
    const merged = useMemo(
        () =>
            [...runRows, ...skipRows]
                .filter((row) =>
                    matchesAutomationRunStatusFacets(
                        row.status,
                        selectedFacets,
                    ),
                )
                .sort(
                    (left, right) =>
                        Date.parse(right.startedAt) -
                            Date.parse(left.startedAt) ||
                        right.id.localeCompare(left.id),
                ),
        [runRows, selectedFacets, skipRows],
    );
    const visibleCount = pageSize * pageCount;
    const rows = useMemo(
        () => merged.slice(0, visibleCount),
        [merged, visibleCount],
    );

    const oldestRunAt = oldestStartedAt(runRows);
    const oldestSkipAt = oldestStartedAt(skipRows);
    const runsHasMore = wantsRuns && runs.hasMore;
    const skipsHasMore = wantsSkips && skips.hasMore;
    const runsLoadMore = runs.loadMore;
    const skipsLoadMore = skips.loadMore;
    const loadMore = useCallback(() => {
        const nextVisibleCount = pageSize * (pageCount + 1);
        const cutoffRow =
            merged.length > nextVisibleCount
                ? merged[nextVisibleCount - 1]
                : undefined;
        // Rows older than the next page's cutoff are already loaded, so only a
        // source read no deeper than the cutoff can still add rows to it.
        const cutoff =
            cutoffRow === undefined
                ? Number.NEGATIVE_INFINITY
                : Date.parse(cutoffRow.startedAt);
        setPageWindow({ key: ledgerKey, pageCount: pageCount + 1 });
        if (runsHasMore && oldestRunAt >= cutoff) {
            runsLoadMore();
        }
        if (skipsHasMore && oldestSkipAt >= cutoff) {
            skipsLoadMore();
        }
    }, [
        ledgerKey,
        merged,
        oldestRunAt,
        oldestSkipAt,
        pageCount,
        pageSize,
        runsHasMore,
        runsLoadMore,
        skipsHasMore,
        skipsLoadMore,
    ]);

    return {
        rows,
        isLoading: (runs.isLoading || skips.isLoading) && rows.length === 0,
        error: runs.error ?? skips.error,
        hasMore: merged.length > rows.length || runsHasMore || skipsHasMore,
        loadMore,
        isLoadingMore: runs.isLoadingMore || skips.isLoadingMore,
    };
}
